import { Link, useLocation } from "react-router-dom";
import { SEO } from "@/components/SEO";
import { WhatsAppButton } from "@/components/WhatsAppButton";

const NAV_LINKS = [
  { to: "/about", label: "About" },
  { to: "/coverage", label: "Coverage" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

const FOOTER_COLS = [
  {
    title: "Company",
    links: [
      { to: "/about", label: "About Us" },
      { to: "/contact", label: "Contact" },
      { to: "/blog", label: "Blog" },
    ],
  },
  {
    title: "Platform",
    links: [
      { to: "/coverage", label: "Coverage" },
      { to: "/login", label: "Sign In" },
      { to: "/register", label: "Get Started" },
    ],
  },
  {
    title: "Legal",
    links: [
      { to: "/privacy", label: "Privacy Policy" },
      { to: "/terms", label: "Terms & Conditions" },
    ],
  },
];

const SOCIAL_LINKS = [
  {
    label: "Twitter", href: "https://twitter.com/SendoraNg",
    icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.74l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>,
  },
  {
    label: "LinkedIn", href: "https://linkedin.com/company/sendora-ng",
    icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>,
  },
  {
    label: "Facebook", href: "https://facebook.com/SendoraNg",
    icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>,
  },
  {
    label: "Instagram", href: "https://instagram.com/SendoraNg",
    icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" /></svg>,
  },
];

export default function PageLayout({ children, title, subtitle, seo = {} }) {
  const location = useLocation();
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex flex-col">
      <SEO {...seo} />
      {/* Navbar */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/sendora-logo.webp" alt="Sendora" className="h-7 w-auto" />
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            {NAV_LINKS.map(l => (
              <Link
                key={l.to}
                to={l.to}
                className={`text-sm font-medium transition-colors ${location.pathname === l.to ? "text-[#0c1e41]" : "text-slate-600 hover:text-[#0c1e41]"}`}
              >
                {l.label}
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm font-medium text-slate-700 hover:text-[#0c1e41] transition-colors hidden sm:block">Sign in</Link>
            <Link to="/register" className="text-sm font-semibold bg-[#0c1e41] text-white hover:bg-[#091629] transition-colors px-4 py-2 rounded-lg shadow-sm">
              Get Started
            </Link>
          </div>
        </div>
      </header>

      {/* Page header */}
      {(title || subtitle) && (
        <div className="bg-gradient-to-br from-[#0c1e41] to-[#061224] text-white py-14">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            {title && <h1 className="text-3xl sm:text-4xl font-extrabold mb-3">{title}</h1>}
            {subtitle && <p className="text-[#adc0e0] text-lg max-w-xl">{subtitle}</p>}
          </div>
        </div>
      )}

      {/* Content */}
      <main className="flex-1">{children}</main>

      <WhatsAppButton />

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div className="col-span-2 md:col-span-1">
              <div className="mb-4">
                <img src="/sendora-logo.webp" alt="Sendora" className="h-7 w-auto brightness-0 invert" />
              </div>
              <p className="text-sm leading-relaxed text-slate-500 mb-4">Nigeria's premier bulk SMS platform. Reliable delivery. Transparent pricing.</p>
              {/* Social links */}
              <div className="flex items-center gap-2">
                {SOCIAL_LINKS.map(s => (
                  <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label}
                    className="w-8 h-8 rounded-full bg-slate-800 hover:bg-[#0c1e41] flex items-center justify-center transition-colors text-slate-400 hover:text-white">
                    {s.icon}
                  </a>
                ))}
              </div>
            </div>
            {FOOTER_COLS.map(col => (
              <div key={col.title}>
                <h4 className="text-white text-sm font-semibold mb-4">{col.title}</h4>
                <ul className="space-y-2 text-sm">
                  {col.links.map(l => (
                    <li key={l.to}><Link to={l.to} className="hover:text-white transition-colors">{l.label}</Link></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="border-t border-slate-800 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm">
            <p>© {new Date().getFullYear()} Sendora. All rights reserved.</p>
            <p className="text-slate-600">Built for Nigerian businesses.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
